import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { format, differenceInDays } from "date-fns";
import { useAPIGetReportCustomerPoint } from "../api/api-get-report-customer-point";
import ReportCustomerPointFilter from "./ReportCustomerPointFilter";

const ReportCustomerPointList = forwardRef((props, ref) => {
  const {
    loading,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    getFilter,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
  } = useAPIGetReportCustomerPoint();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => handleOnUpdateViewRow(row),
    handleOnDeleteViewRow: (row) => handleOnDeleteViewRow(row),
  }));

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // Body templates

  const customerBodyTemplate = (row) => {
    const { data_customer } = row;
    return (
      <div className="col-start-start gap-1">
        <span className="font-semibold text-slate-700">{data_customer?.name}</span>
        <span className="text-xs text-slate-500">{data_customer?.code}</span>
        <span className="text-xs font-semibold text-blue-600">
          BC: {data_customer?.data_bc?.name ?? "-"}
        </span>
      </div>
    );
  };

  const transactionDateBodyTemplate = (row) => {
    const { transaction_date, data_sales } = row;
    return (
      <div className="col-start-start gap-1">
        <span className="text-sm font-semibold text-slate-700">
          {transaction_date ? format(new Date(transaction_date), "dd MMM yyyy") : "-"}
        </span>
        {data_sales?.no && (
          <span className="text-xs text-blue-600">#{data_sales.no}</span>
        )}
      </div>
    );
  };

  const expiredDateBodyTemplate = (row) => {
    const { expired_date, point, usage_point } = row;
    if (!expired_date) return "-";
    const today = new Date();
    const expDate = new Date(expired_date);
    const daysLeft = differenceInDays(expDate, today);
    const isExpired = daysLeft < 0;
    const isNear = daysLeft >= 0 && daysLeft <= 30;
    const isFullyUsed = (usage_point ?? 0) === (point ?? 0);
    return (
      <div className="col-start-start gap-1">
        <span
          className={`text-sm font-semibold ${isFullyUsed ? "text-slate-700" : isExpired ? "text-red-600" : isNear ? "text-orange-500" : "text-slate-700"
            }`}
        >
          {format(expDate, "dd MMM yyyy")}
        </span>
        {!isFullyUsed && (
          <span
            className={`text-xs ${isExpired ? "text-red-500" : isNear ? "text-orange-400" : "text-slate-400"
              }`}
          >
            {isExpired
              ? `Expired ${Math.abs(daysLeft)} days ago`
              : daysLeft === 0
                ? "Expires today!"
                : `${daysLeft} days left`}
          </span>
        )}
      </div>
    );
  };

  const pointBodyTemplate = (row) => {
    const { point, usage_point } = row;
    const remaining = (point ?? 0) - (usage_point ?? 0);
    return (
      <div className="col-start-start gap-1">
        <div className="row-start-center gap-2">
          <span className="text-xs text-slate-500 w-16">Earned:</span>
          <span className="font-semibold text-slate-700 text-sm">
            {new Intl.NumberFormat("id-ID").format(point ?? 0)}
          </span>
        </div>
        <div className="row-start-center gap-2">
          <span className="text-xs text-slate-500 w-16">Used:</span>
          <span className="text-sm text-orange-600">
            {new Intl.NumberFormat("id-ID").format(usage_point ?? 0)}
          </span>
        </div>
        <div className="row-start-center gap-2">
          <span className="text-xs text-slate-500 w-16">Remaining:</span>
          <span
            className={`text-sm font-bold ${remaining > 0 ? "text-green-600" : "text-slate-400"
              }`}
          >
            {new Intl.NumberFormat("id-ID").format(remaining)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <ReportCustomerPointFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={() => {
            const filter = getFilter(filterValue);
            fetchListData({ page: 1, take: paging.take, filter });
          }}
        />
      </div>

      {/* table section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <DataTable
          dataKey="id"
          className="w-100"
          loading={loading}
          showGridlines
          stripedRows
          value={listData}
        >
          <Column
            field="no"
            header="#"
            className="text-center"
            headerClassName="w-[4rem]"
            alignHeader="center"
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            field="data_customer.name"
            header="Customer"
            body={customerBodyTemplate}
          />
          <Column
            field="transaction_date"
            header="Transaction Date"
            body={transactionDateBodyTemplate}
          />
          <Column
            field="expired_date"
            header="Expired Date"
            body={expiredDateBodyTemplate}
          />
          <Column
            field="point"
            header="Point"
            body={pointBodyTemplate}
          />
        </DataTable>
        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={handleOnPage}
        />
      </div>
    </div>
  );
});

export default ReportCustomerPointList;
